import React, { useState } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@core-ui/Components/ui/table.tsx';
import { Skeleton } from '@core-ui/Components/ui/skeleton.tsx';
import { useTranslation } from 'react-i18next';
import { type IntegrationJob, JobStatus } from '@features/dashboard/types/dashboard.types.ts';
import EmptyState from '@features/dashboard/components/shared/EmptyState.tsx';
import JobLogs from '@features/dashboard/components/dashboardIntegrations/JobLogs.tsx';
import { PaginationControls } from '@ui/shared/paginationControls.tsx';
import { cn } from '@core-ui-lib/utils.ts';
import { Badge } from '@core-ui/Components/ui/badge.tsx';
import { RefreshCw } from 'lucide-react';
import { Button } from '@core-ui/Components/ui/button.tsx';

interface Props {
  jobs: IntegrationJob[] | null;
  isLoading: boolean;
  isRetrying: boolean;
  currentPage: number;
  totalCount: number;
  rowsPerPage: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rows: number) => void;
  onRetryJob: (jobId: string) => Promise<void>;
}

const ROWS_PER_PAGE_OPTIONS = [5, 10, 20, 50];
const ERROR_ATTEMPT_NUMBER = 4;
const jobStatus: Record<string, string> = {
  'CREATED': 'bg-blue-500/10 text-blue-700 border-blue-500/30 dark:text-blue-300',
  'SUCCESS': 'bg-emerald-500/10 text-emerald-700 border-emerald-500/30 dark:text-emerald-300',
  'ERROR': 'bg-red-500/10 text-red-700 border-red-500/30 dark:text-red-300',
  'PROCESSING': 'bg-amber-500/10 text-amber-700 border-amber-500/30 dark:text-amber-300',
  'CONFIRMED': 'bg-violet-500/10 text-violet-700 border-violet-500/30 dark:text-violet-300',
};

function TableSkeleton ({ rowCount = 5 }: { rowCount?: number }): React.JSX.Element {
  const skeletonClass = 'bg-muted';
  return (
    <>
      {Array.from({ length: rowCount }).map((_, i) => (
        <TableRow key={i} className="border-b border-border/60">
          <TableCell><Skeleton className={`h-4 w-32 ${skeletonClass}`} /></TableCell>
          <TableCell><Skeleton className={`h-4 w-32 ${skeletonClass}`} /></TableCell>
          <TableCell className="text-center"><Skeleton className={`h-6 w-20 rounded-full inline-block ${skeletonClass}`} /></TableCell>
          <TableCell className="text-center"><Skeleton className={`h-4 w-14 inline-block ${skeletonClass}`} /></TableCell>
          <TableCell className="text-center"><Skeleton className={`h-4 w-14 inline-block ${skeletonClass}`} /></TableCell>
          <TableCell className="text-center"><Skeleton className={`h-4 w-14 inline-block ${skeletonClass}`} /></TableCell>
          <TableCell className="text-center"><Skeleton className={`h-6 w-8 inline-block ${skeletonClass}`} /></TableCell>
        </TableRow>
      ))}
    </>
  );
}

function formatDate (
  dateValue: string | { $date: string } | null | undefined,
  lang: string
): string {
  if (dateValue == null) {
    return '-';
  }

  const localeLower = (lang ?? '').toLowerCase();
  const locale =
      localeLower.startsWith('es') ? 'es-ES' :
        localeLower.startsWith('en') ? 'en-US' :
          'en-US';

  let dateString: string;
  if (typeof dateValue === 'object' && dateValue.$date !== '') {
    dateString = dateValue.$date;
  } else if (typeof dateValue === 'string') {
    dateString = dateValue;
  } else {
    return '-';
  }

  const date = new Date(dateString);
  if (isNaN(date.getTime())) {
    return '-';
  }

  const dateStr = date.toLocaleDateString(locale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  const timeStr = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  });

  return `${dateStr}, ${timeStr}`;
}

export default function IntegrationJobsTable ({
  jobs,
  isLoading,
  isRetrying,
  currentPage,
  totalCount,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  onRetryJob
}: Props): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const [retryRow, setRetryRow] = useState<number | null>(null);

  function handleRetryClick (jobId: string, index: number): void {
    void onRetryJob(jobId);
    setRetryRow(index);
    setTimeout(() => {
      setRetryRow(null);
    }, 1500);
  }

  return (
    <div className="w-full overflow-auto rounded-md border border-border bg-card">
      
      <Table>
        <TableHeader className="bg-muted/30">
          <TableRow className="border-b border-border/60 hover:bg-transparent">
            <TableHead className="text-muted-foreground font-medium">{t('dashboard.integrations.jobsTable.jobId')}</TableHead>
            <TableHead className="text-muted-foreground font-medium">{t('dashboard.integrations.jobsTable.contactList')}</TableHead>
            <TableHead className="text-muted-foreground font-medium text-center">{t('dashboard.integrations.jobsTable.status')}</TableHead>
            <TableHead className="text-muted-foreground font-medium text-center">{t('dashboard.integrations.jobsTable.attempt')}</TableHead>
            <TableHead className="text-muted-foreground font-medium text-center">{t('dashboard.integrations.jobsTable.lastActivity')}</TableHead>
            <TableHead className="text-muted-foreground font-medium text-center">{t('dashboard.integrations.jobsTable.created')}</TableHead>
            <TableHead className="text-muted-foreground font-medium text-center">{t('dashboard.integrations.jobsTable.actions')}</TableHead>
          </TableRow>
        </TableHeader>

        {(jobs?.length === 0 || jobs === null) && (
          <TableBody>
            <TableRow>
              <TableCell colSpan={7}>
                <div className="flex justify-center items-center min-h-[200px]">
                  <EmptyState message={t('dashboard.integrations.jobsTable.empty')} />
                </div>
              </TableCell>
            </TableRow>
          </TableBody>
        )}

        {isLoading ? (
          <TableSkeleton />  
        ) : (
          <TableBody>
            {jobs?.map((job, i) => {
              return (
                <TableRow key={i} className="cursor-pointer border-b border-border/60 hover:bg-muted/50 transition-colors">
                  <TableCell>{job._id}</TableCell>
                  <TableCell>{job.contactListId}</TableCell>
                  <TableCell className="text-center">
                    <Badge variant="outline" className={cn('text-xs px-2 py-0.5 capitalize border', jobStatus[job.status])}>
                      {t(`dashboard.integrations.jobsTable.jobStatus.${job.status}`)}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-center">{job.attempt}</TableCell>
                  <TableCell className="text-center">{formatDate(job.updatedAt.$date, i18n.language)}</TableCell>
                  <TableCell className="text-center">{formatDate(job.createdAt.$date, i18n.language)}</TableCell>
                  {(job.status as JobStatus === JobStatus.ERROR || job.attempt === ERROR_ATTEMPT_NUMBER) && (
                    <TableCell className="text-center">
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={() => {
                          handleRetryClick(job._id, i);
                        }}
                        disabled={retryRow === i && isRetrying}
                        aria-label={t('dashboard.integrations.jobsTable.retry')}
                        title={t('dashboard.integrations.jobsTable.retry')}
                      >
                        <RefreshCw className="w-4 h-4 text-sky-500 hover:text-sky-800"/>
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              );
            })
            }
          </TableBody>
        )}
      </Table>
      {jobs?.map((job, i) => (
        <div key={i}>
          {job.logs.length > 0 && (
            <JobLogs />
          )}
        </div>
      ))}

      <PaginationControls
        mode="server"
        totalItems={totalCount}
        currentPage={currentPage}
        rowsPerPage={rowsPerPage}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        isLoading={isLoading}
        className="border-t border-border/60"
      />

    </div>
  );
}